import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, Send, Loader2 } from "lucide-react";
import { UserSearchSelect } from "./UserSearchSelect";
import { EventSelectForTest } from "./EventSelectForTest";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const STORAGE_KEY_USER = "admin-notification-preview-user";
const STORAGE_KEY_EVENT = "admin-notification-preview-event";

interface EmailPreviewPanelProps {
  templateId?: string;
  subjectTemplate: string;
  bodyTemplate: string;
}

interface PlaceholderValues {
  first_name: string;
  last_name: string;
  email: string;
  event_title: string;
  event_venue: string;
  event_date: string;
  event_time: string;
  event_id: string;
  meeting_point: string;
  agenda: string;
  whatsapp_group: string;
  host_name: string;
  host_bio: string;
}

const DEFAULT_PLACEHOLDERS: PlaceholderValues = {
  first_name: "Friend",
  last_name: "",
  email: "user@example.com",
  event_title: "Sample Event Title",
  event_venue: "Sample Venue",
  event_date: "January 15, 2025",
  event_time: "7:00 PM",
  event_id: "sample-event-id",
  meeting_point: "Main entrance",
  agenda: "• Welcome and introductions\n• Main activity\n• Wrap-up",
  whatsapp_group: "https://chat.whatsapp.com/example",
  host_name: "Sample Host",
  host_bio: "An experienced host passionate about creating memorable experiences.",
};

function processTemplate(text: string, placeholders: PlaceholderValues): string {
  let result = text;
  
  // Handle conditional blocks: {{#variable}}content{{/variable}}
  result = result.replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_, key, content) => {
    const value = placeholders[key as keyof PlaceholderValues];
    return value && value.trim() !== "" ? content : "";
  });
  
  // Replace simple placeholders
  for (const [key, value] of Object.entries(placeholders)) {
    result = result.replace(new RegExp(`{{${key}}}`, "g"), value);
  }
  
  return result;
}

export function EmailPreviewPanel({ templateId, subjectTemplate, bodyTemplate }: EmailPreviewPanelProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY_USER);
  });
  const [selectedEventId, setSelectedEventId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY_EVENT);
  });

  // Persist selections to localStorage
  useEffect(() => {
    if (selectedUserId) {
      localStorage.setItem(STORAGE_KEY_USER, selectedUserId);
    } else {
      localStorage.removeItem(STORAGE_KEY_USER);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedEventId) {
      localStorage.setItem(STORAGE_KEY_EVENT, selectedEventId);
    } else {
      localStorage.removeItem(STORAGE_KEY_EVENT);
    }
  }, [selectedEventId]);

  // Send test notification mutation
  const sendTestMutation = useMutation({
    mutationFn: async () => {
      if (!templateId || !selectedUserId) {
        throw new Error("Please select a user to send the test to");
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("send-test-notification", {
        body: {
          template_id: templateId,
          user_id: selectedUserId,
          event_id: selectedEventId,
        },
      });

      if (response.error) throw response.error;
      if (!response.data.success) throw new Error(response.data.error || "Failed to send test");
      
      return response.data;
    },
    onSuccess: (data) => {
      const successChannels = data.results?.filter((r: any) => r.success).map((r: any) => r.channel) || [];
      toast.success(`Sent via: ${successChannels.join(", ")}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Fetch user data
  const { data: userData } = useQuery({
    queryKey: ["preview-user", selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, email")
        .eq("id", selectedUserId)
        .single();
      return data;
    },
    enabled: !!selectedUserId,
  });

  // Fetch event data
  const { data: eventData } = useQuery({
    queryKey: ["preview-event", selectedEventId],
    queryFn: async () => {
      if (!selectedEventId) return null;
      const { data } = await supabase
        .from("events")
        .select(`
          id,
          title,
          start_time,
          timezone,
          venue,
          agenda_text,
          whatsapp_group,
          host_id,
          profiles!events_host_id_fkey (first_name, last_name, bio),
          event_locations (
            location_id,
            location_type,
            locations (name)
          )
        `)
        .eq("id", selectedEventId)
        .single();
      return data;
    },
    enabled: !!selectedEventId,
  });

  // Build placeholder values from selected user/event or defaults
  const placeholders = useMemo((): PlaceholderValues => {
    const values = { ...DEFAULT_PLACEHOLDERS };

    if (userData) {
      values.first_name = userData.first_name || "Friend";
      values.last_name = userData.last_name || "";
      values.email = userData.email || "";
    }

    if (eventData) {
      const eventDate = new Date(eventData.start_time);
      const hostProfile = eventData.profiles as { first_name: string | null; last_name: string | null; bio: string | null } | null;
      
      // Find meeting point from event_locations
      const locations = eventData.event_locations as Array<{
        location_type: string;
        locations: { name: string } | null;
      }> | null;
      const meetingPointLocation = locations?.find(l => l.location_type === "meeting_point");
      const meetingPoint = meetingPointLocation?.locations?.name || "";
      
      values.event_title = eventData.title || values.event_title;
      values.event_venue = eventData.venue || values.event_venue;
      values.event_date = eventDate.toLocaleDateString("en-GB", { 
        weekday: "long", day: "numeric", month: "long", year: "numeric",
        timeZone: (eventData as any).timezone || "Europe/London"
      });
      values.event_time = eventDate.toLocaleTimeString("en-GB", { 
        hour: "2-digit", minute: "2-digit",
        timeZone: (eventData as any).timezone || "Europe/London"
      });
      values.event_id = eventData.id;
      values.meeting_point = meetingPoint;
      values.agenda = eventData.agenda_text || "";
      values.whatsapp_group = eventData.whatsapp_group || "";
      values.host_name = hostProfile 
        ? [hostProfile.first_name, hostProfile.last_name].filter(Boolean).join(" ") 
        : values.host_name;
      values.host_bio = hostProfile?.bio || "";
    }

    return values;
  }, [userData, eventData]);

  const renderedSubject = processTemplate(subjectTemplate || "No subject", placeholders);
  const renderedBody = processTemplate(bodyTemplate || "No content", placeholders);

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4" />
          Live Preview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* User/Event selectors */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Preview as user</Label>
            <UserSearchSelect
              value={selectedUserId}
              onChange={(userId) => setSelectedUserId(userId)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">With event data</Label>
            <EventSelectForTest
              value={selectedEventId}
              onChange={setSelectedEventId}
            />
          </div>
        </div>

        {/* Email Preview */}
        <div className="border rounded-lg overflow-hidden bg-white">
          {/* Subject bar */}
          <div className="bg-muted px-3 py-2 border-b">
            <p className="text-sm font-medium truncate">{renderedSubject}</p>
          </div>
          
          {/* Body preview */}
          <div 
            className="p-4 text-sm max-h-[400px] overflow-y-auto prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: renderedBody.replace(/\n/g, "<br>") 
            }}
          />
        </div>

        {/* Send Test Button */}
        {templateId && (
          <Button
            onClick={() => sendTestMutation.mutate()}
            disabled={!selectedUserId || sendTestMutation.isPending}
            className="w-full gap-2"
          >
            {sendTestMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Manual Send
          </Button>
        )}

        <p className="text-xs text-muted-foreground">
          {templateId 
            ? "Select a user to preview and manually send a notification with the current template."
            : "Save the template first to enable sending notifications."}
        </p>
      </CardContent>
    </Card>
  );
}
